import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuildById } from '@/features/builds/data/builds';
import { deleteSuiteExecution } from '@/features/executions/data/suiteExecutions';
import { useProjectFilter } from '@/features/projects';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateSuiteExecutions,
  invalidateTestExecutions,
  useAccessToken,
  useInvalidateQueries,
  queryKeys,
} from '@/shared/hooks';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { usePageTitle } from '@/ui/hooks/usePageTitle';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { useSuiteExecutionDetailQuery } from '../hooks/executions';
import { SuiteExecutionDetailContent } from './SuiteExecutionDetailContent';
import type { AuthSession } from '@/auth/types';
import type { ApiError } from '@/shared/data/client';
import type { BuildDto } from '@flowqa/shared';

type SuiteExecutionDetailPageProps = { session: AuthSession | null };

export const SuiteExecutionDetailPage = ({
  session,
}: SuiteExecutionDetailPageProps): ReactElement => {
  const { suiteSlug, runNumber } = useParams<{ suiteSlug: string; runNumber: string }>();
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const token = useAccessToken();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const resolvedSlug = useMemo(() => (suiteSlug ?? '').trim(), [suiteSlug]);
  const lastBreadcrumbKey = useRef<string | null>(null);
  const parsedRunNumber = useMemo(() => {
    const parsed = Number(runNumber);
    return Number.isFinite(parsed) ? parsed : null;
  }, [runNumber]);
  const executionQuery = useSuiteExecutionDetailQuery(
    resolvedSlug || null,
    parsedRunNumber,
    selectedProjectId ?? null,
  );
  const { data: execution, isPending, isError, error } = executionQuery;
  usePageTitle(execution?.name ?? execution?.suiteName ?? 'Suite Execution');

  const buildId = execution?.resultsFile?.buildId ?? null;
  const buildQuery = useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildDetail(buildId, execution?.organisationId ?? null),
    enabled: Boolean(token && buildId),
    queryFn: async () =>
      buildId ? await getBuildById(buildId, execution?.organisationId ?? undefined) : null,
  });
  const build = buildQuery.data ?? null;

  const deleteSuiteExecutionMutation = useMutation<{ id: string }, ApiError, { id: string }>({
    mutationFn: async (input) =>
      await deleteSuiteExecution({ id: input.id }, execution?.organisationId ?? null),
    onSuccess: async () => {
      await invalidateQueries([
        invalidateSuiteExecutions(selectedProjectId ?? null, execution?.organisationId ?? null),
        invalidateTestExecutions(selectedProjectId ?? null, execution?.organisationId ?? null),
        invalidateGridQuery(gridPaths.suiteExecutions),
        invalidateGridQuery(gridPaths.testExecutions),
      ]);
      notify({ message: 'Suite execution deleted.', tone: 'green' });
      const fallbackHref = resolvedSlug ? `/suites/${resolvedSlug}` : '/suites';
      void navigate(fallbackHref, { replace: true });
    },
    onError: (deleteError) => {
      notify({ message: deleteError.message, tone: 'red' });
    },
  });

  const hasInvalidParams = !resolvedSlug || parsedRunNumber === null;
  const showNotFound = hasInvalidParams || (!isPending && executionQuery.isFetched && !execution);

  useEffect(() => {
    if (!execution?.projectId) return;
    if (execution.projectId !== selectedProjectId) {
      setSelectedProjectId(execution.projectId);
    }
  }, [execution, selectedProjectId, setSelectedProjectId]);

  useEffect(() => {
    if (!execution) return () => undefined;
    const nameLabel = execution.suiteName ?? execution.name ?? 'Suite';
    const runLabel =
      execution.runNumber !== null && execution.runNumber !== undefined
        ? `Run ${execution.runNumber}`
        : 'Execution';
    const nextKey = `${execution.id}:${nameLabel}:${runLabel}`;
    if (lastBreadcrumbKey.current === nextKey) return () => undefined;
    lastBreadcrumbKey.current = nextKey;
    const suiteHref = resolvedSlug ? `/suites/${resolvedSlug}` : '/suites';
    setOverrideItems([
      { label: 'Suites', href: '/suites' },
      { label: nameLabel, href: suiteHref },
      { label: runLabel },
    ]);
    return () => {
      clearOverride();
      lastBreadcrumbKey.current = null;
    };
  }, [clearOverride, execution, resolvedSlug, setOverrideItems]);

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Execution not found"
        body="This suite execution does not exist or you no longer have access to it."
        backTo="/suites"
        backLabel="Back to suites"
      />
    );
  }

  const handleDeleteSuiteExecution = async (): Promise<void> => {
    if (!execution) return;
    await deleteSuiteExecutionMutation.mutateAsync({ id: execution.id });
  };

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view suite execution details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load execution"
      data={execution}
    >
      {(resolvedExecution) => (
        <SuiteExecutionDetailContent
          title={
            resolvedExecution.runNumber !== null && resolvedExecution.runNumber !== undefined
              ? {
                  name: resolvedExecution.name ?? resolvedExecution.suiteName ?? 'Suite Execution',
                  runNumber: resolvedExecution.runNumber,
                }
              : (resolvedExecution.name ?? resolvedExecution.suiteName ?? 'Suite Execution')
          }
          execution={resolvedExecution}
          build={build}
          onDelete={handleDeleteSuiteExecution}
          deleteLoading={deleteSuiteExecutionMutation.isPending}
        />
      )}
    </DetailPageScaffold>
  );
};
